import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { Home, Users, User, Info, Wrench, PanelLeftClose, PanelLeftOpen, Swords } from 'lucide-react';
import { auth } from './firebase/config';
import { AppProvider } from './context/AppContext';
import LoginPage from './pages/auth/LoginPage';
import HomePage from './pages/home/HomePage';
import GroupFormPage from './pages/groups/GroupFormPage';
import GroupDetailPage from './pages/groups/GroupDetailPage';
import InvitePage from './pages/groups/InvitePage';
import GameInvitePage from './pages/groups/GameInvitePage';
import GameDetailsPage from './pages/games/GameDetailsPage';
import ProfilePage from './pages/profile/ProfilePage';
import CommunityPage from './pages/community/CommunityPage';
import AboutPage from './pages/about/AboutPage';
import PlayerPage from './pages/players/PlayerPage';
import FixDuplicatesPage from './pages/admin/FixDuplicatesPage';
import { DuelsPage, DuelDetailsPage } from './pages/duels/DuelsPage';
import LoadingOverlay from './components/ui/LoadingOverlay';
import BottomNavBar from './components/layout/BottomNavBar';
import ProfileSetupOverlay from './components/ProfileSetupOverlay';
import InstallPrompt from './components/InstallPrompt';
import UpdateBanner from './components/UpdateBanner';
import { useAppContext } from './context/AppContext';

const ProtectedRoute = ({ user, children }) => {
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const DesktopSidebar = ({ collapsed, onToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useAppContext();
  const navItems = [
    { path: '/', icon: Home, label: 'Início' },
    { path: '/community', icon: Users, label: 'Comunidade' },
    { path: '/duels', icon: Swords, label: 'Confrontos' },
    { path: '/profile', icon: User, label: 'Perfil' },
    ...(profile?.superAdmin ? [{ path: '/admin/fix', icon: Wrench, label: 'Admin' }] : []),
    { path: '/about', icon: Info, label: 'Sobre' },
  ];

  // Quando recolhido: sidebar some, mostra só um botão flutuante para reabrir.
  if (collapsed) {
    return (
      <button
        onClick={onToggle}
        className="hidden lg:flex fixed top-4 left-4 z-30 w-10 h-10 items-center justify-center bg-white border border-slate-200 rounded-xl shadow-md hover:bg-slate-50 text-slate-600 transition-colors"
        title="Mostrar menu"
        aria-label="Mostrar menu"
      >
        <PanelLeftOpen className="w-5 h-5" />
      </button>
    );
  }

  return (
    <div className="hidden lg:flex flex-col w-64 bg-white border-r border-slate-200 fixed h-full z-20">
      <div className="p-6 border-b border-slate-100 relative">
        <img src="/icone.png" alt="Pelada Match" className="w-10 h-10 mb-3" />
        <h1 className="text-lg font-bold text-slate-800">pelada<span className="text-blue-500">match</span></h1>
        <p className="text-xs text-slate-400 mt-0.5">O futebol raiz acontecendo</p>
        <button
          onClick={onToggle}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
          title="Ocultar menu"
          aria-label="Ocultar menu"
        >
          <PanelLeftClose className="w-5 h-5" />
        </button>
      </div>
      <nav className="flex-1 p-3 space-y-1">
        {navItems.map(({ path, icon: Icon, label }) => {
          const active = location.pathname === path;
          return (
            <button
              key={path}
              onClick={() => navigate(path)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${active ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-800'}`}
            >
              <Icon className="w-5 h-5" />
              {label}
            </button>
          );
        })}
      </nav>
      <div className="p-4 border-t border-slate-100">
        <p className="text-[10px] text-slate-400 text-center">v2.2.0 · Pelada Match</p>
      </div>
    </div>
  );
};

const DevBadge = () => {
  if (process.env.NODE_ENV !== 'development') return null;
  const projectId = process.env.REACT_APP_PROJECT_ID || 'dev';
  return (
    <div
      className="fixed bottom-24 left-2 z-50 bg-amber-400 text-amber-900 text-[9px] font-bold px-2 py-0.5 rounded-full shadow pointer-events-none select-none lg:bottom-4"
      title={`Ambiente de desenvolvimento — ${projectId}`}
    >
      DEV · {projectId}
    </div>
  );
};

const AppLayout = ({ children }) => {
  const { profile, loading } = useAppContext();
  const showSetup = !loading && profile === null;
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('sidebarCollapsed') === 'true');
  const toggleSidebar = () => setSidebarCollapsed(c => {
    const next = !c;
    localStorage.setItem('sidebarCollapsed', String(next));
    return next;
  });

  useEffect(() => {
    const syncAppHeight = () => {
      document.documentElement.style.setProperty('--app-height', `${window.innerHeight}px`);
    };

    syncAppHeight();
    window.addEventListener('resize', syncAppHeight);
    window.addEventListener('orientationchange', syncAppHeight);

    return () => {
      window.removeEventListener('resize', syncAppHeight);
      window.removeEventListener('orientationchange', syncAppHeight);
    };
  }, []);

  return (
    <>
      {loading && <LoadingOverlay />}
      {!loading && showSetup && <ProfileSetupOverlay />}
      <div
        className="flex h-full flex-col overflow-hidden bg-slate-50 lg:h-screen lg:flex-row"
        style={{ minHeight: 'var(--app-height, 100dvh)' }}
      >
        <DesktopSidebar collapsed={sidebarCollapsed} onToggle={toggleSidebar} />
        <div
          className={`flex min-h-0 flex-1 flex-col overflow-hidden pb-20 lg:pb-0 transition-all ${sidebarCollapsed ? 'lg:ml-0' : 'lg:ml-64'}`}
          style={{ paddingTop: 'env(safe-area-inset-top)' }}
        >
          {children}
        </div>
      </div>
      <BottomNavBar />
      <InstallPrompt />
      <DevBadge />
    </>
  );
};

function App() {
  const [user, setUser] = useState(undefined); // undefined = loading

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
    });
    return unsub;
  }, []);

  if (user === undefined) return <LoadingOverlay />;

  return (
    <>
    <UpdateBanner />
    <Routes>
      <Route path="/login" element={
        user ? <Navigate to="/" replace /> : <LoginPage />
      } />
      <Route path="/*" element={
        <ProtectedRoute user={user}>
          <AppProvider userId={user?.uid}>
            <AppLayout>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/groups/create" element={<GroupFormPage />} />
                <Route path="/groups/edit/:id" element={<GroupFormPage />} />
                <Route path="/groups/:id" element={<GroupDetailPage />} />
                <Route path="/invite/:groupId" element={<InvitePage />} />
                <Route path="/invite/:groupId/:gameId" element={<GameInvitePage />} />
                <Route path="/games/:groupId/:gameId" element={<GameDetailsPage />} />
                <Route path="/community" element={<CommunityPage />} />
                <Route path="/duels" element={<DuelsPage />} />
                <Route path="/duels/:id" element={<DuelDetailsPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/players/:userId" element={<PlayerPage />} />
                <Route path="/admin/fix" element={<FixDuplicatesPage />} />
              </Routes>
            </AppLayout>
          </AppProvider>
        </ProtectedRoute>
      } />
    </Routes>
    </>
  );
}

export default App;
